import { Component, input, OnInit, output } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import { IndustryPack } from 'app/providers/semantria-api/industry-pack.service';
import { LanguageTemplate } from 'app/providers/semantria-api/language-template.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

export interface AddConfigurationLayerDialogOptions {
  standardConfigs: {
    language: LanguageTemplate[];
    configuration: SemantriaConfiguration[];
    pack: IndustryPack[];
  };
  layeredConfigs: {
    language: LanguageTemplate[];
    configuration: SemantriaConfiguration[];
    pack: IndustryPack[];
  };
}

@EnableClassTelemetry()
@Component({
  selector: 'app-add-configuration-layer-dialog',
  templateUrl: './add-configuration-layer-dialog.component.html',
  styleUrls: ['./add-configuration-layer-dialog.component.scss'],
  standalone: false,
})
export class AddConfigurationLayerDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly data = input<AddConfigurationLayerDialogOptions>();

  readonly modalClosed = output<AddConfigurationLayerDialogOptions | void>();

  readonly entryOrder = (_a, _b) => 0;
  readonly filteredData: AddConfigurationLayerDialogOptions = {
    standardConfigs: {
      language: [],
      configuration: [],
      pack: [],
    },
    layeredConfigs: {
      language: [],
      configuration: [],
      pack: [],
    },
  };
  readonly result: AddConfigurationLayerDialogOptions = {
    standardConfigs: {
      language: [],
      configuration: [],
      pack: [],
    },
    layeredConfigs: {
      language: [],
      configuration: [],
      pack: [],
    },
  };

  search$ = new BehaviorSubject<string>('');

  ngOnInit(): void {
    this.search$
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe((val) => this.filterConfigurations(val));

    this.filterConfigurations();
  }

  filterConfigurations(value = '') {
    const lcValue = value.toLocaleLowerCase();

    Object.keys(this.data()).forEach((ck) =>
      Object.keys(this.data()[ck]).forEach(
        (tk) =>
          (this.filteredData[ck][tk] = this.data()[ck][tk].filter((i) =>
            i.name.toLocaleLowerCase().includes(lcValue)
          ))
      )
    );
  }

  isDataSourceEmpty(templateDs) {
    return !Object.keys(templateDs).reduce((prev, curr) => prev + templateDs[curr].length, 0);
  }

  toggleTemplate(
    dataSourceProp: keyof AddConfigurationLayerDialogOptions,
    type: 'language' | 'configuration' | 'pack',
    template
  ) {
    const templateItems = this.result[dataSourceProp][type];
    if (templateItems.includes(template)) {
      templateItems.splice(templateItems.indexOf(template), 1);
    } else {
      templateItems.push(template);
    }
  }

  isTemplateSelected(
    dataSourceProp: keyof AddConfigurationLayerDialogOptions,
    type: 'language' | 'configuration' | 'pack',
    template
  ) {
    return this.result[dataSourceProp][type].includes(template);
  }

  isSelectionEmpty() {
    return (
      this.isDataSourceEmpty(this.result.standardConfigs) &&
      this.isDataSourceEmpty(this.result.layeredConfigs)
    );
  }

  cancel() {
    this.modalClosed.emit();
  }

  add() {
    this.modalClosed.emit(this.result);
  }
}
